import React, { useState, useContext } from 'react';
import { List, Avatar, Card, Pagination, Skeleton, Typography, Progress } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { AppContext } from 'context/AppContext';
import { Campaign, Enrollment } from 'models/CommonType';
import { useEnrollment, useEnrollmentRankings } from '../../../services/EnrollmentService';

import { ReactComponent as UserLevel1Icon } from 'assets/svg/user-level-1.svg';
import { ReactComponent as UserLevel1CompletedIcon } from 'assets/svg/user-level-1-completed.svg';
import { ReactComponent as UserLevel2Icon } from 'assets/svg/user-level-2.svg';
import { ReactComponent as UserLevel2CompletedIcon } from 'assets/svg/user-level-2-completed.svg';
import { ReactComponent as UserLevel3Icon } from 'assets/svg/user-level-3.svg';
import { ReactComponent as UserLevel3CompletedIcon } from 'assets/svg/user-level-3-completed.svg';
import { ReactComponent as UserLevel4Icon } from 'assets/svg/user-level-4.svg';
import { ReactComponent as UserLevel4CompletedIcon } from 'assets/svg/user-level-4-completed.svg';
import { ReactComponent as UserLevel5Icon } from 'assets/svg/user-level-5.svg';
import { ReactComponent as UserLevel5CompletedIcon } from 'assets/svg/user-level-5-completed.svg';

const { Text, Title } = Typography;

interface RankingsListProps {
  campaign: Campaign;
  authKey: string;
}

const RankingsList: React.FC<RankingsListProps> = ({ campaign, authKey }) => {
  const { t } = useTranslation();
  const { api } = useContext(AppContext);
  const navigate = useNavigate();

  const { data: userDetailsResponse, isLoading: userLoading } = useSWR(
    authKey ? 'user-details' : null,
    () => api.profile.getUserDetails(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const currentUser = userDetailsResponse?.error
    ? null
    : (userDetailsResponse?.results?.[0] || userDetailsResponse) as any;

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: userEnrollment, isLoading: enrollmentLoading } = useEnrollment(
    campaign.id,
    currentUser?.id || '',
    authKey
  );

  const {
    data: rankingsData,
    isLoading: rankingsLoading,
    isError: rankingsError
  } = useEnrollmentRankings(
    campaign.id,
    currentPage,
    pageSize,
    authKey
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getLevelIcon = (levelNumber: number) => {
    if (!userEnrollment) {
      switch (levelNumber) {
        case 1: return <UserLevel1Icon />;
        case 2: return <UserLevel2Icon />;
        case 3: return <UserLevel3Icon />;
        case 4: return <UserLevel4Icon />;
        case 5: return <UserLevel5Icon />;
        default: return <UserLevel1Icon />;
      }
    }

    const campaignLevel = userEnrollment.campaign_level ?? 0;

    switch (levelNumber) {
      case 1:
        return <UserLevel1CompletedIcon />;
      case 2:
        return campaignLevel >= 1 ? <UserLevel2CompletedIcon /> : <UserLevel2Icon />;
      case 3:
        return campaignLevel >= 2 ? <UserLevel3CompletedIcon /> : <UserLevel3Icon />;
      case 4:
        return campaignLevel >= 3 ? <UserLevel4CompletedIcon /> : <UserLevel4Icon />;
      case 5:
        return campaignLevel >= 4 ? <UserLevel5CompletedIcon /> : <UserLevel5Icon />;
      default:
        return <UserLevel1Icon />;
    }
  };

  const getHeadingText = () => {
    if (!userEnrollment) {
      return t('#Campaign.join_message');
    }

    const campaignLevel = userEnrollment.campaign_level ?? 0;
    const maxLevel = 5;

    if (campaignLevel >= 4) {
      return t('#Campaign.top_level_message', { maxLevel });
    } else {
      return t('#Campaign.level_message', { level: campaignLevel + 1, maxLevel });
    }
  };

  const getSubHeadingText = () => {
    if (!userEnrollment) {
      return t('#Campaign.complete_task_message');
    }

    const campaignLevel = userEnrollment.campaign_level ?? 0;
    const milesToNext = userEnrollment.miles_to_next_campaign_level ?? 0;

    if (campaignLevel >= 4 || milesToNext === 0) {
      return '';
    }

    return t('#Campaign.miles_to_go_message', { count: milesToNext, miles: milesToNext });
  };

  const getProgressPercentage = () => {
    if (!userEnrollment) return 0;

    const campaignLevel = userEnrollment.campaign_level ?? 0;

    if (campaignLevel >= 4) return 100;

    const progress = userEnrollment.progress ?? 0;
    return Math.round(progress * 100);
  };

  const renderLevelProgressCard = () => {
    if (!userEnrollment || enrollmentLoading || !currentUser || userLoading) {
      return (
        <div className="level-progress-wrapper">
          {/* Desktop - no card */}
          <div className="level-progress-content desktop-only">
            <Skeleton avatar active paragraph={{ rows: 3 }} />
          </div>
          {/* Mobile - with card */}
          <Card className="level-progress-card mobile-only">
            <Skeleton avatar active paragraph={{ rows: 3 }} />
          </Card>
        </div>
      );
    }

    const campaignLevel = userEnrollment.campaign_level ?? 0;
    const headingText = getHeadingText();
    const subHeadingText = getSubHeadingText();
    const progressPercentage = getProgressPercentage();

    const progressContent = (
      <>
        <div className="level-icons-container">
          {[1, 2, 3, 4, 5].map((levelNum) => (
            <div
              key={levelNum}
              className={`level-icon ${(!userEnrollment && levelNum === 1) ||
                (userEnrollment && campaignLevel >= (levelNum - 1))
                ? 'completed'
                : 'incomplete'
                }`}
            >
              {getLevelIcon(levelNum)}
            </div>
          ))}
        </div>

        <div className="level-text-container">
          <Title level={4} className="level-title">
            {headingText}
          </Title>

          {subHeadingText && (
            <Text className="level-subtitle">
              {subHeadingText}
            </Text>
          )}
        </div>

        {userEnrollment && campaignLevel < 4 && (userEnrollment.miles_to_next_campaign_level ?? 0) > 0 && (
          <Progress
            percent={progressPercentage}
            strokeColor={{
              '0%': '#9BC53D',
              '100%': '#7BA428',
            }}
            trailColor="#f0f0f0"
            strokeWidth={12}
            className="level-progress-bar"
          />
        )}
      </>
    );

    return (
      <div className="level-progress-wrapper">
        {/* Desktop - no card */}
        <div className="level-progress-content desktop-only">
          {progressContent}
        </div>
        {/* Mobile - with card */}
        <Card className="level-progress-card mobile-only">
          {progressContent}
        </Card>
      </div>
    );
  };

  const renderCurrentUserCard = () => {
    if (!userEnrollment || enrollmentLoading || !currentUser || userLoading) {
      return (
        <Card className="current-user-card">
          <Skeleton avatar active paragraph={{ rows: 2 }} />
        </Card>
      );
    }

    return (
      <Card className="current-user-card theme-border">
        <div className="user-info-container">
          <Avatar
            size={60}
            src={userEnrollment.user?.profile?.image_small_url || currentUser?.profile?.image_small_url}
            className="user-avatar clickable-avatar"
            onClick={() => currentUser?.id && handleUserClick(currentUser.id)}
            style={{ cursor: currentUser?.id ? 'pointer' : 'default' }}
          >
            {(userEnrollment.user?.first_name || currentUser?.first_name)?.[0]}
          </Avatar>
          <div className="user-details">
            <div
              className="user-name clickable-name"
              onClick={() => currentUser?.id && handleUserClick(currentUser.id)}
              style={{
                cursor: currentUser?.id ? 'pointer' : 'default',
                color: currentUser?.id ? '#1890ff' : 'inherit'
              }}
            >
              {`${userEnrollment.user?.first_name || currentUser?.first_name || ''} ${userEnrollment.user?.last_name || currentUser?.last_name || ''}`.trim()}
            </div>
            <div className="user-meta">
              {userEnrollment.user?.profile?.bio || currentUser?.profile?.bio || ''}
            </div>
          </div>
          <div className="miles-badge">
            <TrophyOutlined />
            {userEnrollment.miles} {t('common.miles')}
          </div>
        </div>
      </Card>
    );
  };

  const handleUserClick = (userId?: string) => {
    if (!userId) return;

    if (userId === currentUser?.id) {
      navigate('/my-profile');
    } else {
      navigate(`/profile/${userId}`);
    }
  };

  const renderRankingItem = (enrollment: Enrollment) => {
    const isCurrentUser = enrollment.user?.id === currentUser?.id;

    return (
      <List.Item
        key={enrollment.id}
        className={`ranking-item ${isCurrentUser ? 'current-user' : ''}`}
      >
        <List.Item.Meta
          avatar={
            <Avatar
              size={50}
              src={enrollment.user?.profile?.image_small_url}
              className="user-avatar clickable-avatar"
              onClick={() => enrollment.user?.id && handleUserClick(enrollment.user.id)}
              style={{ cursor: enrollment.user?.id ? 'pointer' : 'default' }}
            >
              {enrollment.user?.first_name?.[0]}
            </Avatar>
          }
          title={
            <span
              className="user-name clickable-name"
              onClick={() => enrollment.user?.id && handleUserClick(enrollment.user.id)}
              style={{
                cursor: enrollment.user?.id ? 'pointer' : 'default',
                color: enrollment.user?.id ? '#1890ff' : 'inherit'
              }}
            >
              {`${enrollment.user?.first_name || ''} ${enrollment.user?.last_name || ''}`.trim()}
            </span>
          }
          description={
            <div className="user-meta">
              {enrollment.user?.profile?.bio || ''}
            </div>
          }
        />
        <div className="miles-badge">
          <TrophyOutlined />
          {enrollment.miles} {t('common.miles')}
        </div>
      </List.Item>
    );
  };

  if (rankingsError) {
    return (
      <Card>
        <div className="error-container">
          <Text type="secondary">{t('#Campaign.rankings_error')}</Text>
        </div>
      </Card>
    );
  }

  return (
    <div className="rankings-container">
      {authKey && currentUser && renderLevelProgressCard()}

      {authKey && currentUser && renderCurrentUserCard()}

      <Card className="rankings-list mobile-rankings-card">
        <List
          loading={!!rankingsLoading}
          dataSource={rankingsData?.results || []}
          renderItem={renderRankingItem}
          locale={{
            emptyText: t('#Campaign.no_rankings_available')
          }}
        />

        {rankingsData?.meta?.pagination && rankingsData.meta.pagination.pages > 1 && (
          <div className="pagination-container">
            <Pagination
              current={currentPage}
              total={rankingsData.meta.pagination.total}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total}`
              }
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default RankingsList;